import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { 
  Users, 
  Trash2, 
  Calendar,
  Mail,
  Phone,
  MessageSquare,
  Search,
  Download,
  ClipboardList
} from "lucide-react";
import { toast } from "sonner";
import { getActivityRegistrations, deleteActivityRegistration, getActivities } from "@/lib/api";
import { formatDateTime } from "@/lib/dateUtils";

export default function AdminRegistrations() {
  const [registrations, setRegistrations] = useState([]);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedActivity, setSelectedActivity] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");

  const fetchData = useCallback(async () => {
    try {
      const [regRes, actRes] = await Promise.all([
        getActivityRegistrations(selectedActivity !== "all" ? { activity_id: selectedActivity } : {}),
        getActivities()
      ]);
      setRegistrations(regRes.data);
      setActivities(actRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Fout bij laden van gegevens");
    } finally {
      setLoading(false);
    }
  }, [selectedActivity]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleDelete = async (id) => {
    try {
      await deleteActivityRegistration(id);
      toast.success("Inschrijving verwijderd");
      fetchData();
    } catch (error) {
      console.error("Error deleting registration:", error);
      toast.error("Fout bij verwijderen");
    }
  };

  const exportToCSV = () => {
    const headers = ["Naam", "Email", "Telefoon", "Activiteit", "Opmerkingen", "Datum"];
    const csvData = filteredRegistrations.map(reg => [
      reg.name,
      reg.email,
      reg.phone || "",
      reg.activity_title,
      reg.message || "",
      formatDateTime(reg.created_at)
    ]);

    const csvContent = [
      headers.join(","),
      ...csvData.map(row => row.map(cell => `"${cell}"`).join(","))
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `inschrijvingen_${new Date().toISOString().split("T")[0]}.csv`;
    link.click();
    toast.success("CSV geëxporteerd");
  };

  const filteredRegistrations = registrations.filter(reg => {
    const matchesSearch = searchTerm === "" || 
      reg.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      reg.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      reg.activity_title.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  // Group registrations by activity
  const registrationsByActivity = filteredRegistrations.reduce((acc, reg) => {
    const key = reg.activity_id;
    if (!acc[key]) {
      acc[key] = {
        activity_title: reg.activity_title,
        registrations: []
      };
    }
    acc[key].registrations.push(reg);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-registrations-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Activiteit Inschrijvingen</h1>
          <p className="text-slate-500 mt-1">
            Bekijk alle inschrijvingen voor Sport Eldorado activiteiten
          </p>
        </div>
        <Button
          onClick={exportToCSV}
          variant="outline"
          disabled={filteredRegistrations.length === 0}
          data-testid="export-csv-btn"
        >
          <Download className="w-4 h-4 mr-2" />
          Exporteer CSV
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-white border-slate-200">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-blue-100 rounded-lg">
                <ClipboardList className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Totaal Inschrijvingen</p>
                <p className="text-2xl font-bold text-slate-900">
                  {registrations.length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white border-slate-200">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-green-100 rounded-lg">
                <Calendar className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Activiteiten</p>
                <p className="text-2xl font-bold text-slate-900">
                  {activities.length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white border-slate-200">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-purple-100 rounded-lg">
                <Users className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Unieke Deelnemers</p>
                <p className="text-2xl font-bold text-slate-900">
                  {new Set(registrations.map(r => r.email)).size}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="bg-white border-slate-200">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Zoeken op naam, email of activiteit..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="search-registrations"
                />
              </div>
            </div>
            <Select value={selectedActivity} onValueChange={setSelectedActivity}>
              <SelectTrigger className="w-full sm:w-[250px]" data-testid="activity-filter">
                <SelectValue placeholder="Filter op activiteit" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alle activiteiten</SelectItem>
                {activities.map((activity) => (
                  <SelectItem key={activity.id} value={activity.id}>
                    {activity.title}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Registrations List */}
      {filteredRegistrations.length === 0 ? (
        <Card className="bg-white border-slate-200">
          <CardContent className="py-12 text-center">
            <ClipboardList className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">
              {searchTerm || selectedActivity !== "all" 
                ? "Geen inschrijvingen gevonden met deze filters"
                : "Nog geen inschrijvingen ontvangen"}
            </p>
          </CardContent>
        </Card>
      ) : selectedActivity === "all" ? (
        // Grouped view when showing all
        <div className="space-y-6">
          {Object.entries(registrationsByActivity).map(([activityId, data]) => (
            <Card key={activityId} className="bg-white border-slate-200">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-blue-600" />
                  {data.activity_title}
                  <Badge className="bg-blue-100 text-blue-800 border-blue-200 ml-2">
                    {data.registrations.length} inschrijvingen
                  </Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-slate-200">
                        <th className="text-left py-3 px-4 font-medium text-slate-600">Naam</th>
                        <th className="text-left py-3 px-4 font-medium text-slate-600">Contact</th>
                        <th className="text-left py-3 px-4 font-medium text-slate-600">Opmerkingen</th>
                        <th className="text-left py-3 px-4 font-medium text-slate-600">Datum</th>
                        <th className="text-right py-3 px-4 font-medium text-slate-600">Acties</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.registrations.map((reg) => (
                        <RegistrationRow key={reg.id} registration={reg} onDelete={handleDelete} />
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        // Single table when filtered by activity
        <Card className="bg-white border-slate-200">
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-slate-900">
              Inschrijvingen
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-200">
                    <th className="text-left py-3 px-4 font-medium text-slate-600">Naam</th>
                    <th className="text-left py-3 px-4 font-medium text-slate-600">Contact</th>
                    <th className="text-left py-3 px-4 font-medium text-slate-600">Activiteit</th>
                    <th className="text-left py-3 px-4 font-medium text-slate-600">Opmerkingen</th>
                    <th className="text-left py-3 px-4 font-medium text-slate-600">Datum</th>
                    <th className="text-right py-3 px-4 font-medium text-slate-600">Acties</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRegistrations.map((reg) => (
                    <RegistrationRow key={reg.id} registration={reg} onDelete={handleDelete} showActivity />
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// Registration Row Component
function RegistrationRow({ registration, onDelete, showActivity = false }) {
  return (
    <tr className="border-b border-slate-100 hover:bg-slate-50" data-testid={`registration-row-${registration.id}`}>
      <td className="py-3 px-4">
        <div className="font-medium text-slate-900">{registration.name}</div>
      </td>
      <td className="py-3 px-4">
        <div className="space-y-1">
          <div className="flex items-center gap-2 text-sm text-slate-600">
            <Mail className="w-3 h-3" />
            <a href={`mailto:${registration.email}`} className="hover:text-blue-600">
              {registration.email}
            </a>
          </div>
          {registration.phone && (
            <div className="flex items-center gap-2 text-sm text-slate-500">
              <Phone className="w-3 h-3" />
              <a href={`tel:${registration.phone}`} className="hover:text-blue-600">
                {registration.phone}
              </a>
            </div>
          )}
        </div>
      </td>
      {showActivity && (
        <td className="py-3 px-4 text-slate-600">{registration.activity_title}</td>
      )}
      <td className="py-3 px-4">
        {registration.message ? (
          <div className="flex items-start gap-2 text-sm text-slate-500 max-w-[200px]">
            <MessageSquare className="w-3 h-3 mt-0.5 shrink-0" />
            <span className="truncate">{registration.message}</span>
          </div>
        ) : (
          <span className="text-slate-400 text-sm">-</span>
        )}
      </td>
      <td className="py-3 px-4 text-slate-600 text-sm">
        {formatDateTime(registration.created_at)}
      </td>
      <td className="py-3 px-4 text-right">
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className="text-red-600 hover:text-red-700 hover:bg-red-50"
              data-testid={`delete-registration-${registration.id}`}
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Inschrijving Verwijderen</AlertDialogTitle>
              <AlertDialogDescription>
                Weet je zeker dat je de inschrijving van <strong>{registration.name}</strong> wilt verwijderen?
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Annuleren</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => onDelete(registration.id)}
                className="bg-red-600 hover:bg-red-700"
              >
                Verwijderen
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </td>
    </tr>
  );
}
